import { cloneExperiencePage } from '../duplication/clone.js';
import { nextFreeVariableName } from '../duplication/copy-name.js';
import {
  DEFAULT_FEEDBACK_THANKS,
  EMPTY_BLOCK_VISUAL,
  EXPERIENCE_PAGES_MAX,
  EXPERIENCE_VARIABLE_NAME_MAX,
  type ExperienceBlockDraft,
  type ExperienceContent,
  type ExperiencePage,
  type ExperiencePageDraft,
  FIRST_TEMPS_START,
} from '../schemas/experience.js';
import { isFieldBlock, pageBlocks, pageChoiceName } from './play.js';

/**
 * The DEFAULT TEMPLATE of §5.4, word for word - the page an experience is born
 * with, and the empty protocol page beside it (§9.1).
 *
 * It is a CONSTANT of `shared` and not a builder in one screen, because two
 * screens apply it: the empty editor of §4 (« la carte en pointillé ouvre
 * l'éditeur vide ») and the guided form of §13. Two spellings of « what a new
 * experience looks like » would be two products, and the one that drifted would
 * be the one nobody opens twice.
 *
 * ## What the wording carries, and why it is not decoration
 *
 * Block 2 says the machine AND the character in one sentence, which is H5 (§16)
 * confirmed by §5.4. The global rule « jamais IA » (§11.3) leans on that
 * sentence to let the interviewer stay in role without denying or confirming:
 * without it there is no fiction annoncée, and the rule falls back on its other
 * case. Editing this text is therefore a decision about §11.3, not a wording
 * pass - and the template stays a template: it is edited, reordered and deleted
 * like any other page.
 *
 * ## Why the three fields do not weigh the same
 *
 * The FIRST NAME is mandatory because the interviewer uses it from its first
 * message; the EMAIL is optional because it only serves to reach the person
 * later, and making it a condition of entry would lose interviews for a use
 * that may never come; the CHECKBOX is mandatory because it is the consent.
 * The first name also carries « Nom de la personne dans Sessions » (§9.2, H75):
 * it is the value that names the session in the operator's list.
 *
 * ## Stable ids, and why they are safe
 *
 * The ids below are readable and fixed rather than uuids, because an id has to
 * be unique inside ONE content and nothing more - a variable token addresses a
 * block within the experience that holds it (§8.6). Two experiences born from
 * this template therefore share `consent-firstname`, and nothing anywhere
 * compares them. What it buys is a template that is a constant: no generator to
 * inject, and a fixture that reads.
 */

/** The paragraph of §5.4, verbatim. H5 (§16) is confirmed by it. */
export const DEFAULT_CONSENT_PARAGRAPH =
  'Vous allez converser avec un agent conversationnel, qui peut jouer un personnage. ' +
  "Vos réponses sont enregistrées et lues par l'équipe qui conduit cette étude. " +
  'Vous pouvez arrêter à tout moment en fermant cette page.';

/** The mandatory checkbox of §5.4, verbatim. */
export const DEFAULT_CONSENT_CHECKBOX_LABEL =
  "J'ai lu ce qui précède et j'accepte que cette conversation soit enregistrée";

/**
 * The FEEDBACK page of the template (retour-experience.md §3, issue #176), word
 * for word: ONE temps of normal speed, every block in `fade`, eight blocks in
 * this order, ALL optional - five questions nobody has to answer and the
 * Bouton that sends them.
 *
 * It is a CONSTANT for the reason the consent page is one: the creation of an
 * experience, `withFeedbackPage` (the « + » of the end of the timeline and the
 * migration of the drafts, §7.1, §9) and the MCP `get_experience_template` all
 * apply it, and two spellings would be two products. Its ids are readable and
 * fixed (`page-feedback`, `feedback-title`, ...), safe for the reason the ids
 * of the consent page are; `withFeedbackPage` mints fresh ones when it adds the
 * page to a content that may hold another.
 *
 * The Bouton is the one block of the template whose action is `submit`: the
 * page is SENT, never left (`feedback-page-exit`).
 */
export const DEFAULT_FEEDBACK_PAGE: ExperiencePage = {
  id: 'page-feedback',
  name: "Retour d'expérience",
  kind: 'feedback',
  autoDurationSeconds: null,
  transition: { animation: 'fade', speed: 'normal' },
  visual: {},
  protocol: null,
  conversation: null,
  thanks: DEFAULT_FEEDBACK_THANKS,
  temps: [
    {
      id: 'temps-1',
      speed: 'normal',
      start: FIRST_TEMPS_START,
      blocks: [
        {
          id: 'feedback-title',
          type: 'text',
          animation: 'fade',
          conditions: [],
          visual: { ...EMPTY_BLOCK_VISUAL, textScale: 'large' },
          text: {
            nodes: [
              {
                type: 'paragraph',
                children: [
                  { type: 'text', text: 'Votre avis en une minute', marks: ['bold'], color: null },
                ],
              },
            ],
          },
        },
        {
          id: 'feedback-intro',
          type: 'text',
          animation: 'fade',
          conditions: [],
          visual: EMPTY_BLOCK_VISUAL,
          text: {
            nodes: [
              {
                type: 'paragraph',
                children: [
                  {
                    type: 'text',
                    text: 'Cinq questions, toutes facultatives.',
                    marks: [],
                    color: null,
                  },
                ],
              },
            ],
          },
        },
        {
          id: 'feedback-note-overall',
          type: 'note',
          animation: 'fade',
          conditions: [],
          visual: EMPTY_BLOCK_VISUAL,
          question: "Qu'avez-vous pensé de l'expérience ?",
          required: false,
          variableName: 'note',
        },
        {
          id: 'feedback-note-sincerity',
          type: 'note',
          animation: 'fade',
          conditions: [],
          visual: EMPTY_BLOCK_VISUAL,
          question: "L'expérience vous a-t-elle permis d'être sincère ?",
          required: false,
          variableName: 'sincérité',
        },
        {
          id: 'feedback-relevance',
          type: 'selection',
          animation: 'fade',
          conditions: [],
          visual: EMPTY_BLOCK_VISUAL,
          question: 'Les questions vous ont semblé',
          options: ['Pertinentes', 'En partie', 'Peu pertinentes'],
          required: false,
          variableName: 'pertinence',
        },
        {
          id: 'feedback-duration',
          type: 'selection',
          animation: 'fade',
          conditions: [],
          visual: EMPTY_BLOCK_VISUAL,
          question: "La durée de l'échange",
          options: ['Trop courte', 'Juste bien', 'Trop longue'],
          required: false,
          variableName: 'durée',
        },
        {
          id: 'feedback-comment',
          type: 'input',
          animation: 'fade',
          conditions: [],
          visual: EMPTY_BLOCK_VISUAL,
          label: 'Ce qui vous a plu, ou ce qui vous a manqué',
          lines: 'multi',
          required: false,
          format: 'text',
          variableName: 'avis',
          personName: false,
        },
        {
          id: 'feedback-submit',
          type: 'button',
          animation: 'fade',
          conditions: [],
          visual: EMPTY_BLOCK_VISUAL,
          label: 'Envoyer',
          action: 'submit',
          // Empty: the reader says « Un instant... » while the request waits (#174).
          waitingLabel: '',
          rememberChoice: false,
          variableName: '',
        },
      ],
    },
  ],
};

/**
 * `[Consentement et identité] -> [Protocole] -> [Retour d'expérience]` (§9.1,
 * retour-experience.md §3).
 *
 * ONE temps of normal speed, every block in `fade`: « une page qui demande un
 * accord n'a rien à gagner à se dévoiler par morceaux » (§5.4). The protocol
 * page is born EMPTY, which is permitted in development and refused in
 * production (§9.1). The feedback page closes the timeline, as it always does.
 *
 * « Au style de titre » for the first block is a VISUAL token and not a rich
 * text node: §9.2 closes the list on paragraphs and bullet lists, with no
 * heading, and the size of a passage is exactly what a block override of §9.4
 * is for (`textScale`).
 */
export const DEFAULT_EXPERIENCE_PAGES: ExperiencePage[] = [
  {
    id: 'page-consent',
    name: 'Consentement et identité',
    kind: 'content',
    autoDurationSeconds: null,
    transition: { animation: 'fade', speed: 'normal' },
    visual: {},
    protocol: null,
    conversation: null,
    temps: [
      {
        id: 'temps-1',
        speed: 'normal',
        start: FIRST_TEMPS_START,
        blocks: [
          {
            id: 'consent-title',
            type: 'text',
            animation: 'fade',
            conditions: [],
            visual: { ...EMPTY_BLOCK_VISUAL, textScale: 'large' },
            text: {
              nodes: [
                {
                  type: 'paragraph',
                  children: [
                    { type: 'text', text: 'Avant de commencer', marks: ['bold'], color: null },
                  ],
                },
              ],
            },
          },
          {
            id: 'consent-paragraph',
            type: 'text',
            animation: 'fade',
            conditions: [],
            visual: EMPTY_BLOCK_VISUAL,
            text: {
              nodes: [
                {
                  type: 'paragraph',
                  children: [
                    { type: 'text', text: DEFAULT_CONSENT_PARAGRAPH, marks: [], color: null },
                  ],
                },
              ],
            },
          },
          {
            id: 'consent-firstname',
            type: 'input',
            animation: 'fade',
            conditions: [],
            visual: EMPTY_BLOCK_VISUAL,
            label: 'Votre prénom',
            lines: 'single',
            required: true,
            format: 'text',
            variableName: 'prénom',
            // « Nom de la personne dans Sessions » (§5.4, §9.2, H75): the first
            // name is what names the session in the list, and the template is
            // the one place that ticks it for the operator.
            personName: true,
          },
          {
            id: 'consent-email',
            type: 'input',
            animation: 'fade',
            conditions: [],
            visual: EMPTY_BLOCK_VISUAL,
            label: 'Votre adresse email',
            lines: 'single',
            required: false,
            format: 'email',
            variableName: 'email',
            personName: false,
          },
          {
            id: 'consent-checkbox',
            type: 'checkbox',
            animation: 'fade',
            conditions: [],
            visual: EMPTY_BLOCK_VISUAL,
            label: DEFAULT_CONSENT_CHECKBOX_LABEL,
            required: true,
            variableName: 'consentement',
          },
          {
            id: 'consent-start',
            type: 'button',
            animation: 'fade',
            conditions: [],
            visual: EMPTY_BLOCK_VISUAL,
            label: 'Commencer',
            action: 'next-page',
            // Empty: the reader says « Un instant... » while its advance waits (#174).
            waitingLabel: '',
            rememberChoice: false,
            variableName: '',
          },
        ],
      },
    ],
  },
  {
    id: 'page-protocol',
    name: 'Protocole',
    kind: 'protocol',
    autoDurationSeconds: null,
    transition: { animation: 'fade', speed: 'normal' },
    visual: {},
    protocol: null,
    conversation: null,
    temps: [],
  },
  DEFAULT_FEEDBACK_PAGE,
];

/**
 * Why the feedback page of the template cannot be added to these pages, or null
 * when it can (retour-experience.md §2, §7.1):
 *
 * - `present` - there is one already: at most one per experience;
 * - `full` - the timeline holds the 30 pages of H23, and the feedback page
 *   counts in it (§7.1: « Page de retour » is greyed, with the reason on hover).
 *
 * `present` wins when both hold: it is the reason that does not lift by
 * deleting some other page.
 */
export type FeedbackPageObstacle = 'present' | 'full';

export function feedbackPageObstacle(
  pages: readonly { readonly kind: ExperiencePage['kind'] }[],
): FeedbackPageObstacle | null {
  if (pages.some((page) => page.kind === 'feedback')) return 'present';
  return pages.length >= EXPERIENCE_PAGES_MAX ? 'full' : null;
}

/**
 * The content with the feedback page of the template added LAST, or null when
 * `feedbackPageObstacle` says it cannot be (retour-experience.md §7.1, §9).
 *
 * What the « + » of the end of the timeline and the migration of the drafts both
 * do, hence ONE function: the template cloned with FRESH ids (`mintId`, which
 * the caller may pass - `crypto.randomUUID` by default - because the ids of the
 * template are those of an experience that holds nothing else, and a page added
 * to a timeline that already holds blocks of those ids would break the
 * uniqueness `refuseDuplicateIds` demands) and its variable names SUFFIXED
 * against the whole timeline, « note » -> « note 2 », the rule of the
 * duplication (§4.2, `nextFreeVariableName`). Each result is added to the taken
 * list before the next is asked for, so two names that collide with each other
 * are not both handed the same suffix.
 *
 * It takes the pages of either kind of content, version or draft, and gives the
 * same kind back. The page name and the phrase of thanks are the template's.
 */
export function withFeedbackPage<
  Content extends { readonly pages: readonly ExperiencePageDraft[] },
>(content: Content, mintId: () => string = () => globalThis.crypto.randomUUID()): Content | null {
  if (feedbackPageObstacle(content.pages) !== null) return null;

  // TRIMMED, for the reason the editor gives on its own copy of this list: the
  // schema trims `variableName`, so a stray space must not make a name look free.
  const held: string[] = [];
  for (const page of content.pages) {
    for (const block of pageBlocks(page)) {
      if (isFieldBlock(block) && block.variableName.trim() !== '') {
        held.push(block.variableName.trim());
      }
    }
    const choice = pageChoiceName(page);
    if (choice !== null) held.push(choice);
  }

  const { clone } = cloneExperiencePage(DEFAULT_FEEDBACK_PAGE, mintId);
  const page: ExperiencePageDraft = {
    ...clone,
    temps: clone.temps.map((temps) => ({
      ...temps,
      blocks: temps.blocks.map((block): ExperienceBlockDraft => {
        if (!isFieldBlock(block) || block.variableName === '') return block;
        // Suffixed only when the timeline already holds the name (spec §7.1, §9):
        // a free « note » stays « note », unlike a duplicate, which always has a twin.
        const name = held.includes(block.variableName)
          ? nextFreeVariableName(block.variableName, held, EXPERIENCE_VARIABLE_NAME_MAX)
          : block.variableName;
        held.push(name);
        return { ...block, variableName: name };
      }),
    })),
  };
  return { ...content, pages: [...content.pages, page] };
}

/**
 * A brand new experience, ready to be saved as its v1 (§4: « créer une entité,
 * c'est enregistrer sa v1 »).
 *
 * The theme is left EMPTY rather than filled with the PMS default: an absent
 * token inherits (§9.4), so an empty theme IS the PMS default, and writing the
 * twenty-three tokens into every v1 would freeze today's default into every
 * experience ever created.
 *
 * The pages are CLONED and not handed out: `DEFAULT_EXPERIENCE_PAGES` is one
 * object in one module, and an editor that sorted its blocks in place would
 * rewrite the template for every experience created after it in that process.
 */
export function newExperienceContent(name: string, description = ''): ExperienceContent {
  return {
    name,
    description,
    theme: {},
    persona: null,
    context: '',
    noyauTexts: null,
    pages: structuredClone(DEFAULT_EXPERIENCE_PAGES),
  };
}
